import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import { gql } from 'graphql-tag';
import clsx from 'clsx';
import { useSubscription, useMutation } from '@apollo/client';
import {
  ChatResponse,
  Subscription,
} from '../../../../../../__generated__/loma';
import {
  useAnalyticsPost,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
  NarvarEventTypes,
  useAnalyticsContext,
  NoflakePayload,
  NoflakeTag,
} from '../../../../hooks/Analytics';
import ChatBubble from './UI/ChatBubble';
import ClickableChip from './UI/ClickableChip';
import ChatInput from './UI/ChatInput';
import { z } from 'zod';
import sanitizeHtml from 'sanitize-html';
import SlideableContent from './SlideableContent';
import LoadingDots from './UI/LoadingDots';
import useChatUIState from '../useChatUIState';
import styles from './ChatContent.module.css';

interface ChatContentProps {
  retailerMoniker: string;
  carrierMoniker: string;
  trackingNumber: string;
  orderNumber: string;
}

const responseContentSchema = z.object({
  translationKey: z.string(),
  html: z.string(),
  props: z
    .object({
      prompts: z.string().array().optional(),
      placeholder: z.string().optional(),
    })
    .passthrough()
    .nullable(),
});

const chatMessageSchema = z.object({
  author: z.union([z.literal('NARVAR'), z.literal('USER')]),
  content: responseContentSchema,
});

export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const storedStateSchema = z.object({
  convo: chatMessageSchema.array(),
  state: z.unknown(),
});

type StoredState = z.infer<typeof storedStateSchema>;

export const CHAT_FOOTER_HEIGHT = 80;

const CHAT_SUBSCRIPTION = gql`
  subscription ChatMessageAdded($clientSessionId: ID!) {
    chatMessageAdded(clientSessionId: $clientSessionId) {
      sessionId
      requestId
      sequenceNumber
      isFinal
      state
      content {
        html
        translationKey
        props
      }
    }
  }
`;

const CHAT_MUTATION = gql`
  mutation NewChatMessage($req: ChatRequest!) {
    newChatMessage(req: $req)
  }
`;

function tryGetSessionStorageItem(key: string) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (err) {
    console.error('Error getting sessionStorage', err);
    return null;
  }
}

function trySetSessionStorageItem(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (err) {
    console.error('Error setting sessionStorage', err);
    return;
  }
}

// chat input and conversation display logic.
const ChatContent: FC<ChatContentProps> = ({
  retailerMoniker,
  carrierMoniker,
  trackingNumber,
  orderNumber,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [waitingForResponse, setWaitingForResponse] = useState<boolean>(false);
  const chatInputEl = useRef<HTMLTextAreaElement>(null);
  const chatContainerEl = useRef<HTMLDivElement | null>(null);
  const chatFooterEl = useRef<HTMLDivElement | null>(null);
  const [shouldScrollDown, setShouldScrollDown] = useState<boolean>(false);

  const logEvent = useAnalyticsPost();

  const clientSessionId = [
    retailerMoniker,
    carrierMoniker,
    trackingNumber,
    orderNumber,
  ].join('_');
  const storedState = useMemo(() => {
    const str = tryGetSessionStorageItem(`chat_${clientSessionId}`);
    if (str === null) return;
    const result = storedStateSchema.safeParse(JSON.parse(str));
    if (result.success) {
      return result.data;
    }
  }, [clientSessionId]);

  const baseEventLogInfo = {
    eventComponent: NarvarEventComponents.NTH_KIT_CHAT,
    eventFeature: NarvarEventComponents.NTH_KIT_CHAT,
    feature: 'chatComponent',
    pageComponent: NarvarEventComponents.NTH_KIT_CHAT,
    chatClientSessionId: clientSessionId,
  };

  const [chatConvo, setChatConvo] = useState<ChatMessage[]>(
    storedState?.convo ?? [],
  );

  const [chatError, setChatError] = useState<ChatMessage | null>(null);

  const [chatState, setChatState] = useState<unknown>(
    storedState?.state ?? {
      retailerMoniker,
      carrier: carrierMoniker,
      trackingNumber,
      orderNumber,
    },
  );

  const { state: chatUIState, update: updateChatUIState } = useChatUIState();
  const { hadChatInteraction, chatOpen } = chatUIState;

  useEffect(() => {
    if (chatConvo.length === 0) return;
    const value: StoredState = { convo: chatConvo, state: chatState };
    trySetSessionStorageItem(`chat_${clientSessionId}`, JSON.stringify(value));
  }, [clientSessionId, chatConvo, chatState]);

  // scroll down on initial chat opening only if there's already a convo
  // FIXME: this doesn't work while the chat container is still animating into view.
  // setting a timeout guarantees a scroll, but there may be a better way to do this.
  useEffect(() => {
    if (!chatOpen) return;
    const initialScrollDownTimer = setTimeout(() => {
      if (chatConvo.length > 0) setShouldScrollDown(true);
    }, 600);

    return () => {
      clearTimeout(initialScrollDownTimer);
    };
  }, [chatOpen, chatConvo.length]);

  // whenever the height of the chat content window changes (because there are new messages),
  // scroll down to show the last message.
  // TODO: implement smooth scroll. was seeing some issues in safari that need investigating.
  useEffect(() => {
    if (
      chatContainerEl.current &&
      chatContainerEl.current.scrollHeight &&
      shouldScrollDown
    ) {
      chatContainerEl.current.scrollTop = chatContainerEl.current.scrollHeight;
      updateChatUIState({
        ...chatUIState,
        currentScrollTop: chatContainerEl.current.scrollTop,
      });
      setShouldScrollDown(false);
    }
  }, [shouldScrollDown, updateChatUIState, chatUIState]);

  // we need to determine whether or not the user has already interacted with
  // the chat before deciding to autofocus on input after receiving a new message.
  useEffect(() => {
    if (waitingForResponse || !hadChatInteraction) return;
    chatInputEl.current?.focus();
  }, [waitingForResponse, hadChatInteraction]);

  const { noflakeUrl } = useAnalyticsContext();
  const logResponseEvent = async (body: Partial<NoflakePayload>) => {
    const response = await fetch(noflakeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...body,
    });
    if (!response.ok) {
      throw new Error(`Unexpected response status: ${response.status}`);
    }
  };

  const updateChatError = (newMessage: ChatMessage) => {
    setChatError(newMessage);
    if (chatConvo.length > 0) setShouldScrollDown(true);
  };

  useSubscription<Subscription>(CHAT_SUBSCRIPTION, {
    variables: { clientSessionId },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('There was an error with the subscription: ', error);

      logEvent({
        ...baseEventLogInfo,
        eventType: NarvarEventTypes.ERROR,
        eventFeature: `There was an error with the subscription: ${error.toString()}`,
      });
      setWaitingForResponse(false);
      updateChatError({
        author: 'NARVAR',
        content: {
          html: 'Sorry, I am unable to connect. Please refresh the page.',
          props: null,
          translationKey: 'undefined',
        },
      });
    },
    onData: (response) => {
      const chatMessageAdded = response?.data?.data?.chatMessageAdded;
      if (!chatMessageAdded) return;

      const {
        content,
        isFinal,
        state,
        sessionId,
        requestId,
        sequenceNumber,
      }: ChatResponse = chatMessageAdded;
      if (isFinal) {
        setWaitingForResponse(false);
      }
      if (content) {
        updateChatConvo({
          author: 'NARVAR',
          content: responseContentSchema.parse(content),
        });
      }
      if (state) {
        setChatState(state);
      }
      logResponseEvent({
        tag: NoflakeTag.GEN_AI_RESPONSE_EVENTS,
        event_ts: new Date().toISOString(),
        session_id: sessionId,
        client_session_id: clientSessionId,
        request_id: requestId,
        response_id: sequenceNumber,
        event: 'received',
      }).catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to log response queued event', err);
      });
    },
  });

  const updateChatConvo = (newMessage: ChatMessage) => {
    setChatConvo([...chatConvo, newMessage]);
    if (chatConvo.length > 0) setShouldScrollDown(true);
  };

  const [sendUserInputMutation] = useMutation(CHAT_MUTATION, {
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('There was an error sending the message', error);
      logEvent({
        ...baseEventLogInfo,
        eventType: NarvarEventTypes.ERROR,
        eventFeature: `There was an error sending the message: ${error.toString()}`,
      });
      setWaitingForResponse(false);
      updateChatError({
        author: 'NARVAR',
        content: {
          html: 'Sorry, I am unable to connect. Please refresh the page.',
          props: null,
          translationKey: 'undefined',
        },
      });
    },
    // TODO: we may or may not need this callback. might be useful to
    // trigger some kind of loading state between responses.
    // eslint-disable-next-line no-console
    onCompleted: () => console.info('message sent'),
  });

  // send an empty message to initialize the conversation
  useEffect(() => {
    if (storedState) return;
    sendUserInputMutation({
      variables: {
        req: {
          clientSessionId,
          state: chatState,
          message: '',
        },
      },
      // eslint-disable-next-line no-console
    }).catch((e) => console.error(e));
    // FIXME: properly satisfy this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserInput = (message: string) => {
    sendUserInputMutation({
      variables: {
        req: {
          clientSessionId,
          state: chatState,
          message,
        },
      },
      // eslint-disable-next-line no-console
    }).catch((e) => console.error(e));
  };

  const baseEventClickInfo = {
    ...baseEventLogInfo,
    clickCategory: NarvarClickSubTypes.CHAT_INTERACTION,
    clickType: NarvarClickTypes.INTERACTION,
    eventType: NarvarEventTypes.CLICK,
  };

  // trigger open chat only on input focus when closed
  const onFocusCallback = () => {
    updateChatUIState({
      ...chatUIState,
      hadChatInteraction: true,
      ...(!chatOpen ? { chatOpen: true } : {}),
    });
  };

  const onInputSubmit = (): void => {
    if (chatConvo.length <= 1) {
      logEvent({
        ...baseEventClickInfo,
        clickSubType: NarvarClickSubTypes.CHAT_INPUT_SUBMIT,
      });
    }
    updateUserInput(inputValue);
    updateChatConvo({
      author: 'USER',
      content: { html: inputValue, translationKey: 'undefined', props: null },
    });
    setInputValue('');
    setWaitingForResponse(true);
  };

  const onChipClick = (value: string) => {
    if (chatConvo.length <= 1) {
      logEvent({
        ...baseEventClickInfo,
        clickSubType: NarvarClickSubTypes.CHAT_CHIP_SELECT,
      });
    }
    updateUserInput(value);
    updateChatConvo({
      author: 'USER',
      content: { html: value, translationKey: 'undefined', props: null },
    });
    setWaitingForResponse(true);
    onFocusCallback();
  };

  const defaultPlaceholder = 'Tell us how we can help';
  const placeholder = (() => {
    if (chatConvo.length === 0) {
      return defaultPlaceholder;
    }

    return (
      chatConvo[chatConvo.length - 1].content.props?.placeholder ??
      defaultPlaceholder
    );
  })();

  // The sanitize html call below is deliberately generous in what it accepts.
  // Our primary goal is to ensure there is no XSS. The server is constraining
  // the list of valid elements an attributes.
  return (
    <>
      <SlideableContent
        footerEl={chatFooterEl}
        scrollContainerEl={chatContainerEl}
      >
        <div className={styles.dialogContainer} ref={chatContainerEl}>
          {hadChatInteraction && chatConvo.length > 0 && (
            <div className={styles.gradientOverlay} />
          )}
          <div className={styles.scrollContainer}>
            {(chatError ? [...chatConvo, chatError] : chatConvo).map(
              (chatMessage, i) => {
                const showPrompts =
                  i === chatConvo.length - 1 ||
                  (i === chatConvo.length - 2 &&
                    chatMessage.content.translationKey ===
                      'did_this_help_question');
                return (
                  <div key={`message_${i}`}>
                    {sanitizeHtml(chatMessage.content.html)
                      // if a message contains triple newlines then split it into many bubbles
                      .split('\n\n\n')
                      .map((html, ii) => (
                        <ChatBubble
                          key={`${i}.${ii}`}
                          author={chatMessage.author}
                        >
                          <div
                            // FIXME: we should be santizing this html
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: html,
                            }}
                          />
                        </ChatBubble>
                      ))}
                    {showPrompts &&
                      chatMessage.content.props?.prompts?.length && (
                        <div className={styles.pillsContainer}>
                          {chatMessage.content.props?.prompts?.map(
                            (prompt, i) => (
                              <ClickableChip
                                key={`${prompt}_${i}`}
                                onClick={onChipClick}
                                chipText={prompt}
                                disabled={waitingForResponse}
                              />
                            ),
                          )}
                        </div>
                      )}
                  </div>
                );
              },
            )}
            {waitingForResponse && (
              <ChatBubble key="loading_narvar" author="NARVAR">
                <LoadingDots />
              </ChatBubble>
            )}
            <div key="ref_last_el" />
          </div>
        </div>
      </SlideableContent>
      <div
        className={clsx({
          [styles.footerContainer]: true,
          [styles.chatOpen]: chatOpen,
        })}
        ref={chatFooterEl}
      >
        <div className={styles.inputContainer}>
          <ChatInput
            value={inputValue}
            inputRef={chatInputEl}
            placeholder={placeholder}
            onKeyDown={(e): void => {
              // maps enter key to input submit and removes
              // weird effects of otherwise pressing enter.
              const keyCode = e.code;
              if (keyCode === 'Enter') {
                e.preventDefault();
                if (inputValue) onInputSubmit();
              }
            }}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
              setInputValue(event.target.value)
            }
            onFocus={onFocusCallback}
            onInputButtonClick={(): void => {
              onInputSubmit();
            }}
            disabled={waitingForResponse}
          />
        </div>
      </div>
    </>
  );
};

export default ChatContent;
